/**
 * KPI Cards Component
 * Displays key performance indicators for License Intelligence dashboard
 * Renders a horizontal ribbon layout with 5 KPI cards showing metrics and trends
 * Supports both horizontal (dashboard) and vertical (details) label layouts
 */

import React from 'react';
import { Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import type { KPICardsProps } from './type';
import { KPI_LAYOUTS, RISK_COLORS } from './type';

import './KPICards.css';

/**
 * KPI Cards component displaying key metrics with trends
 * @param {KPICardsProps} props - Component props
 * @param {KPICardData[]} props.cards - Array of KPI card data
 * @param {KPILayout} props.layout - Layout mode: 'horizontal' (default, label on same line) or 'vertical' (label below value)
 * @returns {React.ReactElement} Horizontal ribbon of KPI cards with trends and dividers
 */
function KPICards({
  cards,
  layout = KPI_LAYOUTS.HORIZONTAL,
}: KPICardsProps): React.ReactElement {
  const isVertical = layout === KPI_LAYOUTS.VERTICAL;

  const getRiskInfoClassName = (color?: string): string => {
    switch (color) {
      case RISK_COLORS.CRITICAL:
        return 'kpiRiskInfoCritical';
      case RISK_COLORS.MEDIUM:
        return 'kpiRiskInfoMedium';
      default:
        return '';
    }
  };

  return (
    <div
      className={cx('kpiCardsContainer', {
        'kpiCardsContainer--vertical': isVertical,
      })}>
      {cards.map((card, index) => (
        <React.Fragment key={card.label}>
          <div
            className={cx('kpiCard', {
              'kpiCard--vertical': isVertical,
            })}>
            {/* KPI Header: Value and Label */}
            <div
              className={cx('kpiCardHeader', {
                'kpiCardHeader--vertical': isVertical,
              })}>
              <Typography kind='display4-light'>{card.value}</Typography>
              <Typography kind='body1-bold' className='kpiCardLabel'>
                {card.label}
              </Typography>
            </div>

            {/* KPI Footer: Trend or Risk Info */}
            <div className='kpiCardFooter'>
              {card.trend ? (
                <div className='kpiTrendSection'>
                  <Typography kind='body1' className='kpiTrendValue'>
                    {card.trend.value}
                  </Typography>
                  <div className='kpiTrendBadge'>
                    <Icon
                      kind={
                        card.trend.direction === 'up' ? 'TrendUp' : 'TrendDown'
                      }
                      size='small'
                    />
                  </div>
                  <Typography kind='body2' className='kpiTrendPeriod'>
                    {card.trend.period}
                  </Typography>
                </div>
              ) : (
                <Typography kind='body2' className='kpiRiskInfo'>
                  {Array.isArray(card.riskInfo)
                    ? card.riskInfo.map((part) => (
                        <span
                          key={`${card.label}-risk-${part.text}`}
                          className={getRiskInfoClassName(part.color)}>
                          {part.text}
                        </span>
                      ))
                    : card.riskInfo}
                </Typography>
              )}
            </div>
          </div>
          {index < cards.length - 1 && <div className='kpiDivider' />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default KPICards;
